import { request } from '@/api/apiUtils';
import { createEntityQuery } from '@/data/createEntityQuery';
import type { EntityDefinition, FetcherEntry } from '@/data/types';
import type { IGlobalSearchResponse } from '@/types';

/** Root: query-key prefix for this entity's slice of the cache (ADR 0010).
 *
 *  Search results carry the scene's `code` — so a write that RENUMBERS scenes
 *  (a draft flag, #1840-06) leaves a cached result set naming numbers that no
 *  longer exist. The root exists so that write can name it in the registry
 *  instead of the view reaching for the query client. */
export const GLOBAL_SEARCH_ROOT = ['globalSearch'] as const;

interface GlobalSearchParams {
    projectId: number;
    query: string;
    /** Text-atom type codes the current visibility settings hide. */
    hideFilter: readonly string[];
}

const fetchers = {
    global: {
        key: ({ projectId, query, hideFilter }: GlobalSearchParams) => [...GLOBAL_SEARCH_ROOT, projectId, query, hideFilter] as const,
        fetch: ({ projectId, query, hideFilter }: GlobalSearchParams): Promise<IGlobalSearchResponse> => {
            const filterParams = hideFilter.map(code => `hide_filter[]=${encodeURIComponent(code)}`).join('&');
            const url = `projects/${projectId}/presentations/search?token=${encodeURIComponent(query)}${filterParams ? `&${filterParams}` : ''}`;
            return request<IGlobalSearchResponse>(url);
        },
    },
    // biome-ignore lint/suspicious/noExplicitAny: required for generic type inference
} satisfies Record<string, FetcherEntry<any, any>>;

export const searchEntity: EntityDefinition<typeof fetchers> = {
    name: 'search',
    roots: [GLOBAL_SEARCH_ROOT],
    fetchers,
};

export const useGlobalSearchQuery = createEntityQuery(searchEntity, 'global');

export type { GlobalSearchParams };
